import { useCallback, useMemo, useRef } from 'react';
import {
  CheckStatus,
  computeCheckAll,
  computeCheckedStatus,
} from '../virtual-data/VirtualData';

export type CheckIdsProps<ID> = {
  checkedIds: ID[];
  checkAll: (allChecked: boolean) => void;
  onCheck: (id: ID) => void;
  check: (id: ID | ID[], checked: boolean) => void;
  onToggleCheckAll: () => void;
  checkedStatus: CheckStatus;
  isChecked: (id: ID) => boolean;
};

export const useCheckIds = <ID extends unknown = {}>(
  ids: ID[] | undefined,
  checkedIds: ID[],
  setCheckedIds: (ids: ID[]) => void,
  singleSelect: boolean = false
): CheckIdsProps<ID> => {
  const checkedRef = useRef<ID[]>(checkedIds);
  checkedRef.current = checkedIds;

  const checkAll = useCallback(
    (all: boolean) => {
      if (!singleSelect) {
        if (all) {
          if (ids) {
            setCheckedIds(ids);
          }
        } else {
          setCheckedIds([]);
        }
      }
    },
    [ids, singleSelect]
  );

  const checkedStatus = useMemo(() => {
    return computeCheckedStatus(ids, computeCheckAll(checkedIds));
  }, [ids, checkedIds]);

  const onToggleCheckAll = useCallback(() => {
    if (ids && !singleSelect) {
      if (ids.length > 0) {
        if (checkedStatus === CheckStatus.ALL_CHECKED) {
          setCheckedIds([]);
        } else {
          setCheckedIds(ids);
        }
      }
    }
  }, [ids, singleSelect, checkedIds, checkedStatus]);

  const isChecked = useCallback(
    (id: ID) => {
      return checkedRef.current.indexOf(id) >= 0;
    },
    [ids, checkedIds]
  );

  const check = useCallback(
    (id: ID | ID[], checked: boolean) => {
      const handleSingleId = (singleId: ID, tempCheckedIds: ID[]) => {
        if (singleSelect) {
          tempCheckedIds.push(singleId);
        } else {
          const i = checkedRef.current.indexOf(singleId);
          if (i >= 0) {
            if (!checked) {
              tempCheckedIds.splice(i, 1);
            }
          } else {
            if (checked) {
              tempCheckedIds.push(singleId);
            }
          }
        }
      };

      let tempCheckedIds = [...checkedRef.current];

      if (Array.isArray(id)) {
        id.forEach((singleId) => handleSingleId(singleId, tempCheckedIds));
      } else {
        handleSingleId(id, tempCheckedIds);
      }

      setCheckedIds(tempCheckedIds);
    },
    [ids, singleSelect, checkedIds]
  );

  const onCheck = useCallback(
    (id: ID) => {
      if (singleSelect) {
        setCheckedIds([id]);
      } else {
        const i = checkedRef.current.indexOf(id);
        if (i >= 0) {
          const arr = [...checkedRef.current];
          arr.splice(i, 1);
          setCheckedIds(arr);
        } else {
          setCheckedIds([...checkedRef.current, id]);
        }
      }
    },
    [ids, singleSelect, checkedIds]
  );

  return {
    checkedIds,
    checkAll,
    check,
    onToggleCheckAll,
    checkedStatus,
    onCheck,
    isChecked,
  };
};
